import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { 
  Loader2, AlertCircle, Store, MapPin, ArrowRight, Home, 
  CheckCircle2, XCircle, DollarSign, Clock, Shield
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

import { API_URL } from '../utils/apiConfig';

/**
 * PayPage - Handles QR code scanned payment URLs
 * 
 * URL format: /pay/:code
 * Where :code is the merchant qr_token or UUID
 * 
 * States:
 * 1. loading - Resolving merchant
 * 2. ready - Show merchant info + amount input
 * 3. processing - Processing payment
 * 4. success - Payment completed
 * 5. error - Error occurred
 */
export default function PayPage() {
  const { code } = useParams();
  
  // State
  const [pageState, setPageState] = useState("loading"); // loading, ready, processing, success, error
  const [merchant, setMerchant] = useState(null);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");
  
  // Form state
  const [amount, setAmount] = useState("");
  const [payerName, setPayerName] = useState("");
  const [memo, setMemo] = useState("");
  
  // Payment result
  const [paymentResult, setPaymentResult] = useState(null);

  // Fetch config and resolve merchant on mount
  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        if (!code) {
          throw new Error("missing_code");
        }

        // Fetch config and resolve merchant in parallel
        const [configRes, resolveRes] = await Promise.all([
          fetch(`${API_URL}/api/pay/config`),
          fetch(`${API_URL}/api/pay/resolve/${encodeURIComponent(code)}`)
        ]);

        if (!resolveRes.ok) {
          throw new Error("merchant_not_found");
        }

        const resolveData = await resolveRes.json();
        if (!resolveData.merchant) {
          throw new Error("invalid_response");
        }

        let configData = { min_amount: 1, max_amount: 200, pilot_mode: true };
        if (configRes.ok) {
          configData = await configRes.json();
        }

        if (!cancelled) {
          setMerchant(resolveData.merchant);
          setConfig(configData);
          setPageState("ready");
        }
      } catch (e) {
        console.error("PayPage init error:", e);
        if (!cancelled) {
          setError("Invalid or expired QR code. Please ask the merchant for a new QR.");
          setPageState("error");
        }
      }
    }

    init();
    return () => { cancelled = true; };
  }, [code]);

  // Handle payment submission
  const handlePayment = useCallback(async () => {
    const amountNum = parseFloat(amount);
    
    // Validate amount
    if (!amount || isNaN(amountNum) || amountNum <= 0) {
      setError("Please enter a valid amount");
      return;
    }
    
    if (config && amountNum < config.min_amount) {
      setError(`Minimum amount is $${config.min_amount}`);
      return;
    }
    
    if (config && amountNum > config.max_amount) {
      setError(`Maximum amount is $${config.max_amount}`);
      return;
    }

    setError("");
    setPageState("processing");

    try {
      // Use XMLHttpRequest to avoid fetch body stream issues with platform interceptors
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API_URL}/api/pay/init`, true);
        xhr.setRequestHeader("Content-Type", "application/json");
        
        xhr.onload = function() {
          try {
            const responseData = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve({ ok: true, status: xhr.status, data: responseData });
            } else {
              resolve({ ok: false, status: xhr.status, data: responseData });
            }
          } catch (e) {
            reject(new Error("Invalid response from server"));
          }
        };
        
        xhr.onerror = function() {
          reject(new Error("Network error"));
        };
        
        xhr.send(JSON.stringify({
          code,
          amount: amountNum,
          payer_name: payerName || null,
          memo: memo || null,
        }));
      });

      if (!data.ok) {
        // Handle payment failure (402 status for declined payments)
        if (data.data.detail && typeof data.data.detail === 'object' && data.data.detail.payment_id) {
          // Structured error with payment_id from 402 response
          setPaymentResult({
            ...data.data.detail,
            status: "failed",
          });
          setPageState("error");
        } else if (typeof data.data.detail === 'string') {
          // Simple error message
          throw new Error(data.data.detail);
        } else {
          throw new Error("Payment failed");
        }
        return;
      }

      // Payment success
      setPaymentResult(data.data);
      setPageState("success");
    } catch (e) {
      console.error("Payment error:", e);
      setError(e.message || "Payment failed. Please try again.");
      setPageState("ready");
    }
  }, [code, amount, payerName, memo, config]);

  // Loading State
  if (pageState === "loading") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-blue-900 flex items-center justify-center p-4" data-testid="pay-page-loading">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center" data-testid="pay-loading-card">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" data-testid="loading-spinner" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Loading payment...</h1>
          <p className="text-slate-600">Preparing the payment page.</p>
        </div>
      </div>
    );
  }

  // Success State
  if (pageState === "success" && paymentResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-blue-900 flex items-center justify-center p-4" data-testid="pay-page-success">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full" data-testid="pay-success-card">
          {/* Success Icon */}
          <div className="text-center mb-6">
            <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" data-testid="success-icon" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="success-title">Payment Successful!</h1>
            
            {/* Simulated Badge */}
            {paymentResult.is_simulated && (
              <div className="mt-2 inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">
                <Shield className="w-3 h-3" />
                SIMULATED PAYMENT
              </div>
            )}
          </div>

          {/* Payment Details */}
          <div className="bg-slate-50 rounded-xl p-4 mb-6 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-slate-600">Amount</span>
              <span className="text-2xl font-bold text-emerald-600">
                ${paymentResult.amount?.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-600">Paid to</span>
              <span className="font-medium text-slate-900">
                {paymentResult.merchant?.name}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-600">Payment ID</span>
              <span className="font-mono text-sm text-slate-700">
                {paymentResult.payment_id}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-600">Time</span>
              <span className="text-sm text-slate-700">
                {new Date(paymentResult.created_at).toLocaleString()}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3">
            <Link to="/">
              <Button className="w-full bg-emerald-600 hover:bg-emerald-700 h-12">
                <Home className="w-4 h-4 mr-2" />
                Done
              </Button>
            </Link>
          </div>

          {/* Footer */}
          <div className="mt-6 pt-4 border-t border-slate-100 text-center">
            <p className="text-xs text-slate-400">
              Secured by PeCashGlobal • Transaction ID: {paymentResult.payment_id}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error State (including payment failure)
  if (pageState === "error") {
    const isPaymentError = paymentResult?.status === "failed";
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-blue-900 flex items-center justify-center p-4" data-testid="pay-page-error">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center" data-testid="pay-error-card">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            {isPaymentError ? (
              <XCircle className="w-8 h-8 text-red-600" data-testid="payment-failed-icon" />
            ) : (
              <AlertCircle className="w-8 h-8 text-red-600" data-testid="error-icon" />
            )}
          </div>
          
          <h1 className="text-2xl font-bold text-slate-900 mb-2" data-testid="error-title">
            {isPaymentError ? "Payment Failed" : "Page Not Available"}
          </h1>
          
          <p className="text-slate-600 mb-4" data-testid="error-message">
            {isPaymentError 
              ? "Your payment could not be processed."
              : error || "Invalid or expired QR code."}
          </p>

          {/* Payment failure details */}
          {isPaymentError && paymentResult && (
            <div className="bg-red-50 rounded-xl p-4 mb-6 text-left">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Amount</span>
                  <span className="font-medium">${paymentResult.amount?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Merchant</span>
                  <span className="font-medium">{paymentResult.merchant?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Reference</span>
                  <span className="font-mono text-xs">{paymentResult.payment_id}</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-3">
            {isPaymentError ? (
              <Button 
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={() => {
                  setPaymentResult(null);
                  setError("");
                  setPageState("ready");
                }}
              >
                Try Again
              </Button>
            ) : null}
            <Link to="/">
              <Button variant={isPaymentError ? "outline" : "default"} className={`w-full ${!isPaymentError ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}>
                <Home className="w-4 h-4 mr-2" />
                Go Home
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Processing State
  if (pageState === "processing") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-blue-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-8 h-8 text-emerald-600 animate-spin" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Processing Payment</h1>
          <p className="text-slate-600">
            Please wait while we process your ${parseFloat(amount).toFixed(2)} payment to {merchant?.name}...
          </p>
          
          {config?.pilot_mode && (
            <div className="mt-4 inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">
              <Shield className="w-3 h-3" />
              SIMULATED PAYMENT
            </div>
          )}
        </div>
      </div>
    );
  }

  // Ready State - Show merchant and payment form
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-blue-900 flex items-center justify-center p-4" data-testid="pay-page-ready">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full" data-testid="pay-page-card">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <Store className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="pay-merchant-title">
            Pay {merchant?.name || "Merchant"}
          </h1>
          
          {/* Pilot Mode Badge */}
          {config?.pilot_mode && (
            <div className="mt-2 inline-flex items-center gap-1 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold" data-testid="pilot-mode-badge">
              <Shield className="w-3 h-3" />
              SIMULATED PAYMENT (PILOT)
            </div>
          )}
        </div>

        {/* Merchant Info Card */}
        <div className="border border-slate-200 rounded-xl p-4 mb-6" data-testid="merchant-info-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <Store className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="font-semibold text-slate-900" data-testid="merchant-name">
                {merchant?.name || "Merchant"}
              </p>
            </div>
          </div>
          
          {merchant?.location && (
            <div className="flex items-center gap-2 text-sm text-slate-600" data-testid="merchant-location">
              <MapPin className="w-4 h-4" />
              <span>{merchant.location}</span>
            </div>
          )}

          {merchant?.is_verified && (
            <div className="mt-3 inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium" data-testid="merchant-verified-badge">
              <CheckCircle2 className="w-3 h-3" />
              Verified Merchant
            </div>
          )}
        </div>

        {/* Payment Form */}
        <div className="space-y-4 mb-6">
          {/* Amount Input */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Amount (USD)
            </label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                type="number"
                step="0.01"
                min={config?.min_amount || 1}
                max={config?.max_amount || 200}
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="pl-10 h-12 text-lg font-semibold"
                data-testid="payment-amount-input"
              />
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Min: ${config?.min_amount || 1} • Max: ${config?.max_amount || 200}
            </p>
          </div>

          {/* Payer Name (Optional) */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Your Name <span className="text-slate-400">(optional)</span>
            </label>
            <Input
              type="text"
              placeholder="Enter your name"
              value={payerName}
              onChange={(e) => setPayerName(e.target.value)}
              maxLength={100}
              data-testid="payment-payer-name"
            />
          </div>

          {/* Memo (Optional) */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Memo <span className="text-slate-400">(optional)</span>
            </label>
            <Input
              type="text"
              placeholder="What's this payment for?"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              maxLength={500}
              data-testid="payment-memo"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}
        </div>

        {/* Pay Button */}
        <Button 
          className="w-full bg-emerald-600 hover:bg-emerald-700 h-12 text-base"
          onClick={handlePayment}
          disabled={!amount || parseFloat(amount) <= 0}
          data-testid="payment-submit-button"
        >
          {amount && parseFloat(amount) > 0 ? (
            <>
              Pay ${parseFloat(amount).toFixed(2)}
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          ) : (
            "Enter amount to pay"
          )}
        </Button>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-slate-100 text-center">
          <div className="flex items-center justify-center gap-2 text-xs text-slate-400">
            <Clock className="w-3 h-3" />
            <span>Payments are instant</span>
            <span>•</span>
            <span>Secured by PeCashGlobal</span>
          </div>
        </div>
      </div>
    </div>
  );
}
